/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {injectIntl} from 'react-intl';
import queryString from 'query-string';

import {Video, DiscoverAnalytics, DiscoverUtils} from 'discover-hub';
import LinksService from '../../services/LinksService';
import {getEnvironment} from '../../../utils/Environment';
import {error, success} from '@react/react-spectrum/Toast';
import {setToastPlacement} from '@react/react-spectrum/Toast/js/state';
import DiscoverAnalyticsReact from '../../../utils/DiscoverAnalytics';
import open from '../../../utils/open';
import getConfig from '../../../config';
import './Discover.scss';
import {DeviceDetectionUtil} from 'cchome-shared/utils';

/* eslint-enable no-unused-vars */

type Props = {
	locale: string;
	env: string;
	route: Object;
	history: any;
	location: any;
	intl: any;
};

type State = {
	message: any;
	ingestData: any;
};

declare let adobeIMS: AdobeIMS;

const analytics = new DiscoverAnalyticsReact();

class PlayerPage extends Component<Props, State> {
	onReceiveMessage: Function;

	constructor(props) {
		super(props);
		this.state = {
			message: {},
			ingestData: {}, // populated in discover hub when server has finished getting article data
		};
		setToastPlacement('bottom');
	}

	/**
	 * Returns IMS Data required for making authorized server requests for discover-hub
	 * @returns {object}
	 */
	getIMSData() {
		return {
			bearerToken: adobeIMS.getAccessToken(),
			// @ts-ignore
			userId: window.cchome.util.getUserId(),
		};
	} // TODO: MAKE THIS A COMMON FUNCTION between discover.js and article.js

	/**
	 * Callback function to be passed into discover-hub
	 * component to allow route changes.
	 *
	 * @param {any} arg1
	 * @param {bool} isArticle
	 */
	onDiscoverPathChange(arg1) {
		// This is an array, contain the signals
		// [{type: "topic", value: "photography"},
		// {type: "pageIndex", value: 1},
		// {type: "discoverCategory", value: "inspiration"}]
		const signals = arg1;
		let newPath = this.props.location.pathname; // ex. /cc/en/discover/inspiration
		const newState: any = {};

		let isArticle = false;
		let isVideo = false;
		const newSearchParams = signals.reduce((params, signal) => {
			// For Article pages, the only signal we should really be getting is Article signals anyway
			// TODO: MAKE COMMON THIS LOGIC BETWEEN DISCOVER.JS AND ARTICLE.JS
			if (signal.type === 'topic') {
				// ..?topic=photography
				newState.topic = signal.value;

				if (signal.value !== 'all-topics') {
					params.filter = signal.value;
				}
			}
			else if (signal.type === 'pageIndex') {
				// ..?page=1
				newState.pageIndex = signal.value;

				if (signal.value !== 0) {
					params.page = signal.value + 1;
				}
			}
			else if (signal.type === 'discoverCategory') {
				// cc/en/discover/inspiration
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = signal.value === 'featured' ? '' : `${signal.value}/`;
				newPath = `${beforeDiscover}/discover/${extraPath}`;
				newState.category = signal.value;
			}
			else if (signal.type === 'article') {
				// push this path with go to a different route
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = `${signal.value.articleSlug}`;
				newPath = `${beforeDiscover}/discover/article/${extraPath}`; // NOTE: This drops all query params

				isArticle = true;
			}
			else if (signal.type === 'video') {
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = `${signal.value.videoSlug}/${signal.value.videoId}`;
				newPath = `${beforeDiscover}/discover/video/${extraPath}`;

				isVideo = true;
			}
			return params;
		}, {});

		if (isArticle || isVideo) {
			this.props.history.push({
				pathname: `${newPath}`,
			});
			// this.setState(newState);
		}
		else {
			this.props.history.push(`${newPath}${DiscoverUtils.getSearchPath(newSearchParams)}`);
			// this.setState(newState);
		}
	}

	render() {
		let component;

		if (DeviceDetectionUtil.isInternetExplorer()) {
			component = (
				<div className='ie-error-page'>
					<p>{this.props.intl.formatMessage({id: 'app.ie11.error.message'})}</p>
				</div>
			);
		}
		else {
			const path = this.props.location.pathname.split('/'); // todo: figure out a better way to do this
			const indexOfVideo = path.indexOf('video');
			const videoData = {
				videoSlug: path[indexOfVideo + 1],
				videoId: +path[indexOfVideo + 2],
			};
			let videoTopic = 'all-topics';
			if (this.props.location && this.props.location.state && this.props.location.state.topic) {
				videoTopic = this.props.location.state.topic;
			}

			component = (
				<div className='discover-video-container logged-in'>
					<Video
						isLoggedIn
						videoData={videoData}
						onIngestData={ingestData =>
							Object.keys(this.state.ingestData).length === 0 && this.setState({ingestData})
						}
						onReceiveMessage={this.onReceiveMessage}
						message={this.state.message}
						locale={this.props.locale}
						imsData={this.getIMSData()}
						env={getEnvironment()}
						currentTab={this.props.location.state ? this.props.location.state.currentTab : null}
						onDiscoverPathChange={data => this.onDiscoverPathChange(data)}
						intl={this.props.intl}
						logger={window.cchome.util.logger}
						topic={videoTopic}
						postAnalytics={data => analytics.postCustomData(data)}
					/>
				</div>
			);
		}

		return component;
	}
}

function mapStateToProps() {
	return {};
}

export default connect(mapStateToProps, {})(injectIntl(PlayerPage));
